import React, { useState, useEffect } from 'react';
import { Drawer, Descriptions, Table, Tag, Typography, Alert, Spin, Space, Button, notification } from 'antd';
import { AppstoreOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

import billingService from '../../../services/billingService';
import AssortmentModal from '../Orders/AssortmentModal';

const { Text } = Typography;

// El despacho guarda solo el día: se toma la parte YYYY-MM-DD para que la zona
// horaria del navegador no lo corra al día anterior.
const formatDate = (value) =>
  value ? dayjs(value.slice(0, 10)).format('DD/MM/YYYY') : '-';

const formatCount = (value) =>
  value === null || value === undefined ? '-' : Number(value).toLocaleString('es-CO');

const DISPATCH_STATUS_TAG = {
  ACTIVE: { color: 'success', label: 'Activo' },
  VOID: { color: 'error', label: 'Anulado' },
};

// Producto y variante en una sola columna, como en la factura.
const lineName = (line) => [line.product_name, line.variant].filter(Boolean).join(' ') || '-';

// La ventana del surtido de la orden espera los componentes en su propia forma.
const toAssortmentRows = (components) =>
  components.map((component) => ({
    product_name: component.product_name,
    variant_label: component.variant,
    bunches: component.bunches_per_box,
    stems_per_bunch: component.stems_per_bunch,
    unit_price: component.unit_price ?? null,
  }));

// El despacho no guarda la unidad de cobro: si sus componentes congelaron un
// precio, el surtido se cobraba por tallo.
const assortmentBillingUnit = (line) =>
  line.components.some((component) => component.unit_price !== null && component.unit_price !== undefined) ? 'TALLO' : undefined;

const DispatchDetailDrawer = ({ dispatchId, open, onClose }) => {
  const [dispatch, setDispatch] = useState(null);
  const [loading, setLoading] = useState(false);
  const [assortmentLine, setAssortmentLine] = useState(null);

  useEffect(() => {
    if (!open || !dispatchId) return;
    let cancelled = false;
    setDispatch(null);
    setLoading(true);
    billingService.obtenerDespacho(dispatchId)
      .then((response) => { if (!cancelled) setDispatch(response?.data || null); })
      .catch((error) => {
        if (!cancelled) {
          notification.error({
            title: 'Error',
            description: error?.response?.data?.mensaje || 'No se pudo cargar el despacho.',
          });
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, dispatchId]);

  const statusTag = dispatch && DISPATCH_STATUS_TAG[dispatch.status];
  const hasLiveLines = dispatch?.lines?.some((line) => line.is_live);

  const columns = [
    {
      title: 'Producto',
      key: 'product',
      render: (_, record) => (
        <Space size={4} wrap>
          <Text strong>{lineName(record)}</Text>
          {record.is_assorted && (
            <Button size="small" type="link" icon={<AppstoreOutlined />} onClick={() => setAssortmentLine(record)}>
              Surtido
            </Button>
          )}
        </Space>
      ),
    },
    {
      title: 'Tipo de caja',
      dataIndex: 'packaging_name',
      key: 'packaging_name',
      render: (value) => value || '-',
    },
    {
      title: 'Marca',
      dataIndex: 'mark_code',
      key: 'mark_code',
      render: (value) => value || '-',
    },
    {
      title: 'Cajas',
      dataIndex: 'total_boxes',
      key: 'total_boxes',
      align: 'right',
      render: formatCount,
    },
    {
      title: 'Ramos',
      dataIndex: 'total_bunches',
      key: 'total_bunches',
      align: 'right',
      render: formatCount,
    },
    {
      title: 'Tallos',
      dataIndex: 'total_stems',
      key: 'total_stems',
      align: 'right',
      render: formatCount,
    },
  ];

  return (
    <Drawer
      title={dispatch ? `Despacho ${dispatch.display_number || ''}` : 'Despacho'}
      placement="right"
      size="large"
      open={open}
      onClose={onClose}
      destroyOnHidden
    >
      {loading && <div style={{ textAlign: 'center', padding: 48 }}><Spin /></div>}

      {dispatch && (
        <>
          {hasLiveLines && (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              title="Este despacho es anterior a la actualización del sistema; se muestran los datos actuales de la orden."
            />
          )}

          <Descriptions size="small" column={{ xs: 1, sm: 2 }} style={{ marginBottom: 16 }}>
            <Descriptions.Item label="Número">{dispatch.display_number || '-'}</Descriptions.Item>
            <Descriptions.Item label="Estado">
              {statusTag ? <Tag color={statusTag.color}>{statusTag.label}</Tag> : dispatch.status}
            </Descriptions.Item>
            <Descriptions.Item label="Fecha">{formatDate(dispatch.date)}</Descriptions.Item>
            <Descriptions.Item label="Orden">{dispatch.order?.display_number || '-'}</Descriptions.Item>
            <Descriptions.Item label="Cliente">{dispatch.client?.name || '-'}</Descriptions.Item>
            <Descriptions.Item label="Fito">{dispatch.fito_display || '-'}</Descriptions.Item>
            {dispatch.invoice && (
              <Descriptions.Item label="Factura">
                <Space size={4}>
                  {dispatch.invoice.display_number}
                  {dispatch.invoice.status === 'VOID' && <Tag color="error">Anulada</Tag>}
                </Space>
              </Descriptions.Item>
            )}
          </Descriptions>

          <Table
            size="small"
            columns={columns}
            dataSource={dispatch.lines}
            rowKey={(record) => record.dispatch_detail_id}
            pagination={false}
            scroll={{ x: 640 }}
            summary={() => (
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={3} align="right">
                  <Text strong>Total</Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={3} align="right">
                  <Text strong>{formatCount(dispatch.total_boxes)}</Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={4} />
                <Table.Summary.Cell index={5} align="right">
                  <Text strong>{formatCount(dispatch.total_stems)}</Text>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            )}
          />
        </>
      )}

      {assortmentLine && (
        <AssortmentModal
          open
          isReadOnly
          billingUnit={assortmentBillingUnit(assortmentLine)}
          quantity={assortmentLine.total_boxes}
          initialComponents={toAssortmentRows(assortmentLine.components)}
          onCancel={() => setAssortmentLine(null)}
          onSave={() => setAssortmentLine(null)}
        />
      )}
    </Drawer>
  );
};

export default DispatchDetailDrawer;
